import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { ApiService, ParamsObject } from '../../utils/api.service';
import { Member, Room, RoomBooking, ServiceBooking } from '../../models';
import { map } from 'rxjs/operators';
import { HttpParams } from '@angular/common/http';
import {
  CrudArrayActionServiceMandatory,
  Param,
  ParamProperty,
} from '../../../shared/crud-array-action/abstract-crud-array-action.directive';

@Injectable()
export class RoomBookingService implements CrudArrayActionServiceMandatory {

  public reinitializePageLoading: Subject<void> = new Subject<void>();

  private triggerEditAction: Subject<any> = new Subject<any>();

  private triggerDeleteAction: Subject<any> = new Subject<any>();

  private triggerViewAction: Subject<any> = new Subject<any>();

  // eslint-disable-next-line @typescript-eslint/member-ordering
  public readonly paramAction: Param = {
    edit: {
      provide: true,
      triggerAction: this.triggerEditAction,
    },
    delete: {
      provide: true,
      triggerAction: this.triggerDeleteAction,
    },
    view: {
      provide: true,
      triggerAction: this.triggerViewAction,
    },
  };

  private path = 'room_bookings';

  constructor(
    private apiService: ApiService,
  ) {
  }

  public getTrigger(type: ParamProperty): Observable<any> {
    const typeCapitalize = type.charAt(0).toUpperCase() + type.slice(1);
    const subject = `trigger${typeCapitalize}Action`;
    return (this[subject]).asObservable();
  }

  delete(roomBookingId: number): Observable<any> {
    return this.apiService.delete(`${this.path}/${roomBookingId}`, '', ['roomBookings']);
  }

  get(id: string): Observable<RoomBooking> {
    return this.apiService.get(`${this.path}/${id}`)
      .pipe(map(data => new RoomBooking(data)));
  }

  public convertDataToSendToTrigger(data: any): any {
    return data;
  }

  public sendTriggerEdit(data: number): void {
    this.paramAction.edit.triggerAction.next(data);
  }

  public sendTriggerDelete(data: number): void {
    this.paramAction.delete.triggerAction.next(data);
  }

  public sendTriggerView(data: number): void {
    this.paramAction.view.triggerAction.next(data);
  }

  /**
   * Return all room bookings taking place at the passed day
   */
  getActivesByDate(date: Date, buildingId: string): Observable<RoomBooking[]> {
    const earlyDate = new Date(date.setHours(0, 0, 0, 0));
    const lateDate = new Date(date.setHours(24, 0, 0, 0));

    const query: ParamsObject = {
      building: buildingId,
      from: earlyDate.toISOString(),
      isActive: 'true',
      to: lateDate.toISOString(),
    };

    return this.getAll(query);
  }

  getAll(params: ParamsObject = {}): Observable<RoomBooking[]> {
    const httpParameters = new HttpParams({
      fromObject: params,
    });

    return this.apiService.getSave(this.path, [httpParameters, 'roomBookings'])
      .pipe(
        map(data => {
          data['hydra:member'].map(obj => new RoomBooking(obj));

          return data;
        }),
      );
  }

  getReporting(params: ParamsObject): Observable<any> {
    const httpParameters = new HttpParams({
      fromObject: params,
    });

    return this.apiService.download(`${this.path}/extract` , httpParameters);
  }

  /**
   * Collection of service bookings attached to the room booking
   *
   * @param id room booking id
   */
  getServiceBookings(id: number): Observable<ServiceBooking[]> {
    const params = {
      roomBooking: `${id}`,
    };

    const httpParams = new HttpParams({
      fromObject: params,
    });

    return this.apiService.get('room_booking_service_bookings', httpParams);
  }

  create(roomBooking: RoomBooking): Observable<RoomBooking> {
    return this.apiService.post('room_bookings', roomBooking, '', ['roomBookings'])
      .pipe(
        map(data => new RoomBooking(data)),
      );
  }

  edit(roomBooking: RoomBooking): Observable<RoomBooking> {
    if (typeof roomBooking.owner === 'string') {
      roomBooking.owner = new Member({id: roomBooking.owner.split('/').pop()});
    }

    if (typeof roomBooking.room === 'string') {
      roomBooking.room = new Room({id: (roomBooking.room as string).split('/').pop()});
    }

    return this.apiService.put(`room_bookings/${roomBooking.id}`, roomBooking, '', ['roomBookings'])
      .pipe(
        map(data => new RoomBooking(data)),
      );
  }

  public setReinitializePageLoading(): void {
    this.reinitializePageLoading.next();
  }

  public getReinitializePageLoading(): Observable<void> {
    return this.reinitializePageLoading.asObservable();
  }

  public isPropertyDisplay(property: ParamProperty, roomBooking: RoomBooking): boolean {
    const isCourseInstanceBooking = roomBooking.courseInstance != null;

    if (roomBooking.deletedAt != null) {
      return false;
    }

    if (isCourseInstanceBooking && property !== 'view') {
      return false;
    }

    if (property === 'view') {
      return isCourseInstanceBooking || new Date(roomBooking.endDate) < new Date();
    }

    return !((property === 'edit' || property === 'delete') && new Date(roomBooking.endDate) < new Date());
  }
}
